import React from "react";
import Icon from "@imminently/decisively-ui/Icon";
import { Link, matchPath, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import DropdownMenu from "@imminently/decisively-ui/DropdownMenu";
import { useLocalStorage } from "react-use";
import SidebarNav from "@imminently/decisively-ui/SidebarNav";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/ui/primitives/collapsible";
import { WorkspaceSelector } from "@/ui/blocks/WorkspaceSelector";
import { SidebarLogo } from "./SidebarLogo";
import { AppInfo } from "./AppInfo";
import { cn } from "@/lib/utils";
import { isFeatureEnabled, isFeatureOrParentEnabled, type FeatureSettingPath } from "@/config";
import { useTheme } from "@/app/providers/ThemeContext";
import { AdapterWorkflowSidebarItem, isAdapterWorkflowPath } from "@/features/adapters";

interface SidebarItem {
  name: string;
  path: string | string[];
  icon?: React.ReactNode;
  translationKey?: string;
  active?: boolean;
}

interface SidebarGroup {
  name: string;
  icon?: React.ReactNode;
  open?: boolean;
  items: SidebarItem[];
}

interface Route {
  sidebar?: {
    name: string;
    icon?: React.ReactNode;
    translationKey?: string;
  };
  path: string | string[];
  group?: {
    name: string;
    icon?: React.ReactNode;
    open?: boolean;
  };
  feature?: FeatureSettingPath;
}

interface AppSidebarProps {
  routes: Route[];
  location: any;
  match: any;
  className?: string;
  children: React.ReactNode;
}

interface AppSidebarContextValue {
  collapsed: boolean;
  toggleSidebar: () => void;
}

const AppSidebarContext = React.createContext<AppSidebarContextValue | null>(null);

export const useAppSidebar = () => {
  const context = React.useContext(AppSidebarContext);
  if (!context) {
    throw new Error("useAppSidebar must be used within AppSidebar.");
  }
  return context;
};

const groupRoutes = (routes: Route[], location: any): (SidebarItem | SidebarGroup)[] => {
  const acc: (SidebarItem | SidebarGroup)[] = [];

  for (const route of routes
    .filter(r => r.sidebar)
    .filter(r => !r.feature || isFeatureOrParentEnabled(r.feature))) {
      const { sidebar, path, group } = route;
      const active = matchPath(location.pathname, { path, exact: true })?.isExact || false;
      const item: SidebarItem = {
        name: sidebar!.name,
        path,
        icon: sidebar!.icon,
        active,
        translationKey: sidebar!.translationKey,
      };

      if (group) {
        const existingGroup = acc.find(
          i => 'items' in i && i.name === group.name
        ) as SidebarGroup | undefined;

        if (existingGroup) {
          existingGroup.items.push(item);
        } else {
          acc.push({
            name: group.name,
            icon: group.icon,
            open: group.open,
            items: [item],
          });
        }
      } else {
        acc.push(item);
      }
    }

  return acc;
};

const SidebarItems: React.FC<{ items: (SidebarItem | SidebarGroup)[] }> = ({ items }) => {
  const { search } = useLocation();
  const { t } = useTranslation();
  const sidebarNavContext = SidebarNav.useSidebarNav();
  const isCollapsed = Boolean(sidebarNavContext?.collapsed);

  return (
    <>
      {items.map((item, index) => {
        if ('items' in item) {
          // Group with subitems
          const hasActive = item.items.some(it => it.active);

          if (isCollapsed) {
            return (
              <SidebarNav.MenuItem key={`sidebar-group-${item.name}`}>
                <DropdownMenu>
                  <DropdownMenu.Trigger asChild>
                    <SidebarNav.MenuButton tooltip={item.name} isActive={hasActive} collapsed={isCollapsed} className="py-1.5 h-auto cursor-pointer">
                      {item.icon}
                    </SidebarNav.MenuButton>
                  </DropdownMenu.Trigger>
                  <DropdownMenu.Content side="right" align="start" sideOffset={8} asChild>
                    <div className="p-1">
                      {item.items.map(subItem => {
                        const label = subItem.translationKey ? t(subItem.translationKey) : subItem.name;
                        const link = (subItem.path instanceof Array ? subItem.path[0] : subItem.path) + search;
                        return (
                          <DropdownMenu.Item key={`sidebar-dropdown-${subItem.name}`} asChild>
                            <Link to={link} className="text-sm! font-bold!">
                              <DropdownMenu.Item.Label>{label}</DropdownMenu.Item.Label>
                              <DropdownMenu.Item.Icon>{subItem.icon as any}</DropdownMenu.Item.Icon>
                            </Link>
                          </DropdownMenu.Item>
                        );
                      })}
                    </div>
                  </DropdownMenu.Content>
                </DropdownMenu>
              </SidebarNav.MenuItem>
            );
          }

          return (
            <Collapsible key={`sidebar-group-${item.name}`} defaultOpen={item.open || hasActive}>
              <SidebarNav.MenuItem>
                <CollapsibleTrigger asChild>
                  <SidebarNav.MenuButton tooltip={item.name} collapsed={isCollapsed} className="py-1.5 h-auto cursor-pointer">
                    {item.icon}
                    <span>{item.name}</span>
                    <Icon name="go-forward" className="ml-auto transition-transform duration-200 in-data-[state=open]:rotate-90" />
                  </SidebarNav.MenuButton>
                </CollapsibleTrigger>
                <CollapsibleContent>
                  <SidebarNav.MenuSub>
                    {item.items.map(subItem => {
                      const label = subItem.translationKey ? t(subItem.translationKey) : subItem.name;
                      const link = (subItem.path instanceof Array ? subItem.path[0] : subItem.path) + search;
                      return (
                        <SidebarNav.MenuSubItem key={`sidebar-item-${subItem.name}`}>
                          <SidebarNav.MenuSubButton asChild isActive={subItem.active} className="pl-4 py-1 h-auto">
                            <Link to={link}>
                              <span>{label}</span>
                            </Link>
                          </SidebarNav.MenuSubButton>
                        </SidebarNav.MenuSubItem>
                      );
                    })}
                  </SidebarNav.MenuSub>
                </CollapsibleContent>
              </SidebarNav.MenuItem>
            </Collapsible>
          );
        } else {
          // Single item
          const label = item.translationKey ? t(item.translationKey) : item.name;
          const link = (item.path instanceof Array ? item.path[0] : item.path) + search;
          return (
            <SidebarNav.MenuItem key={`sidebar-item-${item.name}`}>
              <SidebarNav.MenuButton tooltip={label} asChild isActive={item.active} collapsed={isCollapsed} className="py-1.5 h-auto cursor-pointer">
                <Link to={link}>
                  {item.icon as any}
                  <span>{label}</span>
                </Link>
              </SidebarNav.MenuButton>
            </SidebarNav.MenuItem>
          );
        }
      })}
    </>
  );
};

const SidebarContentWrapper: React.FC<{ routes: Route[]; location: any }> = ({ routes, location }) => {
  const sidebarNavContext = SidebarNav.useSidebarNav();
  const isCollapsed = Boolean(sidebarNavContext?.collapsed);
  const isCompact = isFeatureEnabled('design.compact');
  const showAdapterWorkflow = isAdapterWorkflowPath(location.pathname);

  const menuItems = groupRoutes(routes, location);

  return (
    <>
      <SidebarNav.Header className="px-1 py-2">
        <WorkspaceSelector collapsed={isCollapsed} />
      </SidebarNav.Header>
      <SidebarNav.Content className={cn("p-4 scrollable", isCollapsed && "p-2")}>
        <SidebarNav.Group className="p-0">
          <SidebarNav.GroupContent>
            <SidebarNav.Menu className={cn("pt-4 gap-2", isCompact && "pt-0")}>
              {showAdapterWorkflow && <AdapterWorkflowSidebarItem collapsed={isCollapsed} />}
              <SidebarItems items={menuItems} />
            </SidebarNav.Menu>
          </SidebarNav.GroupContent>
        </SidebarNav.Group>
      </SidebarNav.Content>
      <SidebarNav.Footer className={cn("p-4 flex-row gap-2 items-center", isCollapsed && "p-2")}>
        <SidebarLogo />
        <AppInfo />
      </SidebarNav.Footer>
    </>
  );
};

const AppSidebarContent: React.FC<Pick<AppSidebarProps, "routes" | "location" | "match">> = ({ routes, location, match }) => {
  return (
    <SidebarNav.Panel>
      <SidebarContentWrapper routes={routes} location={location} />
    </SidebarNav.Panel>
  );
};

const isEditableShortcutTarget = (target: EventTarget | null) => {
  if (!(target instanceof HTMLElement)) {
    return false;
  }

  return Boolean(
    target.isContentEditable ||
      target.closest('input, textarea, select, [contenteditable="true"], [data-slate-editor="true"], [role="textbox"]'),
  );
};

export const AppSidebar: React.FC<AppSidebarProps> = ({ routes, location, match, className, children }) => {
  const [sidebarOpen, setSidebarOpen] = useLocalStorage("immi.sidebar.open", false);
  const { appearance } = useTheme();
  const sidebarTheme = appearance === "classic" ? "dark" : "light";
  const hasSidebarItems = routes.length > 0;
  const collapsed = !Boolean(hasSidebarItems && sidebarOpen);
  const toggleSidebar = React.useCallback(() => {
    if (hasSidebarItems) {
      setSidebarOpen(collapsed);
    }
  }, [collapsed, hasSidebarItems, setSidebarOpen]);

  React.useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "b" && (event.metaKey || event.ctrlKey) && !isEditableShortcutTarget(event.target)) {
        event.preventDefault();
        toggleSidebar();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [toggleSidebar]);

  return (
    <AppSidebarContext.Provider value={{ collapsed, toggleSidebar }}>
      <div data-sidebar-theme={sidebarTheme} className={cn("flex min-h-full w-full", className)}>
        <SidebarNav
          theme={sidebarTheme}
          collapsible="icon"
          collapsed={collapsed}
        >
          <AppSidebarContent routes={routes} location={location} match={match} />
        </SidebarNav>
        {children}
      </div>
    </AppSidebarContext.Provider>
  );
};
